import React, { useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const STATUS_STYLE = {
  requested: 'bg-slate-100 text-slate-700',
  received: 'bg-amber-100 text-amber-800',
  verified: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
  overdue: 'bg-red-100 text-red-800',
};

const todayISO = () => new Date().toISOString().slice(0, 10);

// Credentials panel for an evaluation. Visible to the completing supervisor and
// HR/Admin; lets them view uploaded files, verify/reject, upload on behalf of the
// employee, and issue the return-window link. On Verify a confirmation dialog
// captures the license/qualification details; only then does the verified file
// flow to the employee's Licensing / Firearms Qualification record (v4.33.3).
export const CredentialsPanel = ({ evaluationId }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [returnResult, setReturnResult] = useState(null); // no-email dialog payload
  const [notifying, setNotifying] = useState(false);
  const fileInputs = useRef({});

  // ── Verify dialog state ───────────────────────────────────────────────────
  const [vTarget, setVTarget] = useState(null);   // the credential item being verified
  const [vCtx, setVCtx] = useState(null);          // { kind, allowed_types, prefill }
  const [vForm, setVForm] = useState({});
  const [vPreview, setVPreview] = useState(null);  // { action, summary }
  const [vBusy, setVBusy] = useState(false);

  // ── On-behalf firearms qualification entry (no card) ──────────────────────
  const [qTarget, setQTarget] = useState(null);
  const [qForm, setQForm] = useState({});
  const [qBusy, setQBusy] = useState(false);
  const openQual = (item) => { setQForm({ qualification_date: '', range_or_instructor: '', result: '', score: '' }); setQTarget(item); };
  const qValid = () => !!(qForm.qualification_date && qForm.qualification_date <= todayISO() &&
    (qForm.range_or_instructor || '').trim() && (qForm.result === 'Pass' || qForm.result === 'Fail') &&
    (!(qForm.score || '').trim() || (qForm.score || '').length <= 20));
  const submitQual = async () => {
    if (!qValid()) return;
    setQBusy(true);
    try {
      await api.post(`/evaluations/${evaluationId}/credentials/${qTarget.item_key}/qualification`, qForm);
      toast.success('Qualification entered on behalf of the officer');
      setQTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not enter the qualification');
    } finally { setQBusy(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/evaluations/${evaluationId}/credentials`);
      setData(data);
    } catch (e) {
      if (e.response?.status !== 403) toast.error('Could not load credentials');
      setData({ forbidden: e.response?.status === 403, items: [] });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [evaluationId]);

  // Refresh the update/create preview whenever the license fields change.
  useEffect(() => {
    if (!vTarget || vCtx?.kind !== 'license') return;
    const { license_type, state, license_number } = vForm;
    if (!license_type || !state || !license_number) { setVPreview(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(
          `/evaluations/${evaluationId}/credentials/${vTarget.id}/verify-preview`,
          { license_type, state, license_number });
        if (!cancelled) setVPreview(data);
      } catch { if (!cancelled) setVPreview(null); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line
  }, [vTarget, vCtx, vForm.license_type, vForm.state, vForm.license_number]);

  if (!data) return null;
  if (data.forbidden) return null;
  if (!data.enabled) return null;

  const viewFile = async (item, fileId) => {
    try {
      const q = fileId ? `?file_id=${encodeURIComponent(fileId)}` : '';
      const res = await api.get(`/evaluations/${evaluationId}/credentials/${item.id}/file${q}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Could not open the file'); }
  };

  const act = async (fn, msg) => { setBusy(msg); try { await fn(); await load(); } finally { setBusy(''); } };

  // ── Verify flow ─────────────────────────────────────────────────────────
  const openVerify = async (item) => {
    setVTarget(item); setVCtx(null); setVPreview(null); setVForm({});
    try {
      const { data } = await api.get(`/evaluations/${evaluationId}/credentials/${item.id}/verify-context`);
      setVCtx(data);
      if (data.kind === 'license') {
        setVForm({
          license_type: data.prefill?.license_type || data.allowed_types?.[0] || '',
          state: data.prefill?.state || '',
          license_number: data.prefill?.license_number || '',
          expiration_date: data.prefill?.expiration_date || '',
        });
      } else {
        setVForm({
          qualification_date: vCtx?.entered?.qualification_date || data.entered?.qualification_date || '',
          range_or_instructor: data.entered?.range_or_instructor || '',
          result: data.entered?.result || '',
          score: data.entered?.score || '',
          roster_date: '',
          verified_against_roster: false,
        });
      }
    } catch (e) {
      setVTarget(null);
      toast.error(e.response?.data?.detail || 'Could not open the verify dialog');
    }
  };
  const closeVerify = () => { setVTarget(null); setVCtx(null); setVPreview(null); setVForm({}); };

  // Client-side guards that mirror the server, so Verify is blocked up front.
  const licenseExpired = vCtx?.kind === 'license' && vForm.expiration_date && vForm.expiration_date < todayISO();
  const qualFuture = vCtx?.kind === 'firearms' && vForm.qualification_date && vForm.qualification_date > todayISO();
  const qualFailed = vCtx?.kind === 'firearms' && vForm.result === 'Fail';

  const verifyValid = () => {
    if (vCtx?.kind === 'license') {
      return !!(vForm.license_type && (vForm.state || '').trim().length === 2 &&
                (vForm.license_number || '').trim() && vForm.expiration_date && !licenseExpired);
    }
    if (vCtx?.kind === 'firearms') {
      const scoreOk = !(vForm.score || '').trim() || (vForm.score || '').length <= 20;
      const base = !!(vForm.qualification_date && !qualFuture && vForm.result === 'Pass' &&
                      (vForm.range_or_instructor || '').trim() && scoreOk);
      if (!base) return false;
      if (vCtx.has_card === false) {
        return !!(vForm.verified_against_roster && (vForm.roster_date || '').trim());
      }
      return true;
    }
    return false;
  };

  const submitVerify = async () => {
    if (!verifyValid()) return;
    setVBusy(true);
    try {
      const { data } = await api.post(
        `/evaluations/${evaluationId}/credentials/${vTarget.id}/verify`, vForm);
      const msg = data.action === 'update' ? 'Verified — updated the matching license record'
        : data.action === 'create' ? 'Verified — created a new license record'
        : data.action === 'firearms' ? 'Verified — firearms qualification recorded on the employee'
        : 'Verified';
      toast.success(msg);
      closeVerify();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not verify');
    } finally {
      setVBusy(false);
    }
  };

  const reject = (item) => { setRejectReason(''); setRejectTarget(item); };

  const submitReject = () => {
    const reason = rejectReason.trim();
    if (reason.length < 10) return;
    const item = rejectTarget;
    setRejectTarget(null);
    return act(async () => {
      await api.post(`/evaluations/${evaluationId}/credentials/${item.id}/reject`, { reason });
      toast.success('Rejected — now send the return link to the officer below');
    }, 'reject');
  };

  // Batched: sends ONE email covering every rejected-but-not-yet-returned item,
  // restarts the return window, and surfaces the no-email case to the verifier.
  const notifyReturn = async () => {
    setNotifying(true);
    try {
      const { data: res } = await api.post(`/evaluations/${evaluationId}/credentials/return-notify`);
      if (res.no_email) {
        setReturnResult(res);
      } else {
        toast.success(`Return link emailed to ${res.email} — ${res.items.length} item(s), due ${new Date(res.deadline).toLocaleDateString()}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the return link');
    } finally { setNotifying(false); }
  };

  const uploadOnBehalf = (item, file) => act(async () => {
    const form = new FormData();
    form.append('file', file);
    await api.post(`/evaluations/${evaluationId}/credentials/${item.item_key}/upload`, form,
      { headers: { 'Content-Type': 'multipart/form-data' } });
    toast.success('Uploaded on behalf of employee');
  }, 'upload');

  const issueReturnLink = () => act(async () => {
    const { data } = await api.post(`/evaluations/${evaluationId}/credentials/return-link`);
    await navigator.clipboard?.writeText(data.link).catch(() => {});
    toast.success(`Upload link copied (valid ${new Date(data.expires_at).toLocaleDateString()})`);
  }, 'return');

  return (
    <Card data-testid="credentials-panel">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Credentials</span>
          {!data.available && <Badge className="bg-red-100 text-red-800" data-testid="credentials-unavailable">Uploads unavailable</Badge>}
        </CardTitle>
        <CardDescription>Documents the employee is asked to provide before signing.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.items.length === 0 && <p className="text-sm text-slate-500">No documents requested.</p>}
        {data.items.map((item) => (
          <div key={item.id || `${item.item_key}-${item.state || 'none'}`} className="flex flex-wrap items-center gap-3 border-b pb-3 last:border-0"
               data-testid={`credential-row-${item.item_key}${item.state ? '-' + item.state : ''}`}>
            <div className="min-w-[160px] flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-slate-800">{item.item_label}</span>
                <Badge className="bg-indigo-100 text-indigo-800" data-testid={`credential-type-badge-${item.item_key}${item.state ? '-' + item.state : ''}`}>
                  {item.credential_type || item.item_label}{item.state ? ` · ${item.state}` : ''}
                </Badge>
              </div>
              {Array.isArray(item.files) && item.files.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-2" data-testid={`credential-files-${item.item_key}${item.state ? '-' + item.state : ''}`}>
                  {item.files.map((f) => (
                    <button key={f.file_id} type="button"
                            className="text-xs text-sky-700 underline hover:text-sky-900"
                            onClick={() => viewFile(item, f.file_id)}
                            data-testid={`credential-filelink-${f.file_id}`}>
                      {f.label ? `${f.label}: ` : ''}{f.display_name}
                    </button>
                  ))}
                </div>
              )}
              {item.uploaded_by_name && (
                <div className="text-xs text-slate-500">
                  {item.uploaded_on_behalf ? `Uploaded by ${item.uploaded_by_name} on behalf of employee` : `Uploaded by ${item.uploaded_by_name}`}
                </div>
              )}
              {item.entered && (
                <div className="text-xs text-slate-500" data-testid={`credential-entered-${item.item_key}`}>
                  Officer entered: {[item.entered.license_type, item.entered.state,
                    item.entered.license_last4 && `#…${item.entered.license_last4}`,
                    item.entered.expiration_date && `exp ${item.entered.expiration_date}`,
                    item.entered.qualification_date, item.entered.range_or_instructor,
                    item.entered.result, item.entered.score].filter(Boolean).join(' · ')}
                  {item.entered_on_behalf && item.entered_by_name ? ` — by ${item.entered_by_name} on behalf of employee` : ''}
                </div>
              )}
              {Array.isArray(item.corrections) && item.corrections.length > 0 && (
                <div className="text-xs text-indigo-600" data-testid={`credential-corrections-${item.item_key}`}>
                  Verifier corrected: {item.corrections.map((c) => c.field).join(', ')}
                </div>
              )}
              {item.verification_method && (
                <div className="text-xs text-slate-500" data-testid={`credential-method-${item.item_key}`}>
                  Verified against {item.verification_method === 'roster' ? 'range roster' : 'uploaded card'}
                </div>
              )}
              {item.kind === 'firearms' && item.texas_note && (
                <div className={`mt-1 text-xs rounded px-2 py-1 ${item.texas_prominent ? 'bg-amber-100 text-amber-900 font-medium border border-amber-300' : 'text-slate-600 bg-slate-50'}`}
                     data-testid={`credential-texas-note-${item.item_key}`}>
                  {item.texas_note}
                </div>
              )}
              {item.status === 'rejected' && item.reject_reason && (
                <div className="text-xs text-red-600">Rejected: {item.reject_reason}</div>
              )}
            </div>
            <Badge className={item.returned_to_employee ? 'bg-sky-100 text-sky-800' : (STATUS_STYLE[item.status] || 'bg-slate-100')} data-testid={`credential-status-${item.item_key}${item.state ? "-" + item.state : ""}`}>
              {item.returned_to_employee ? 'Returned to employee' : item.status}
            </Badge>
            <div className="flex items-center gap-2">
              {item.id && item.has_file !== false && (
                <Button size="sm" variant="outline" onClick={() => viewFile(item)} data-testid={`credential-view-${item.item_key}${item.state ? "-" + item.state : ""}`}>View</Button>
              )}
              {item.id && item.status !== 'verified' && (
                <Button size="sm" disabled={!!busy} onClick={() => openVerify(item)} data-testid={`credential-verify-${item.item_key}${item.state ? "-" + item.state : ""}`}>Verify</Button>
              )}
              {item.id && item.status !== 'rejected' && (
                <Button size="sm" variant="outline" disabled={!!busy} onClick={() => reject(item)} data-testid={`credential-reject-${item.item_key}${item.state ? "-" + item.state : ""}`}>Reject</Button>
              )}
              {data.available && (
                <>
                  <input ref={(el) => (fileInputs.current[item.id || item.item_key] = el)} type="file"
                         accept=".jpg,.jpeg,.png,.heic,.pdf" className="hidden"
                         data-testid={`credential-file-${item.item_key}${item.state ? "-" + item.state : ""}`}
                         onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadOnBehalf(item, f); e.target.value = ''; }} />
                  <Button size="sm" variant="ghost" disabled={!!busy}
                          onClick={() => fileInputs.current[item.id || item.item_key]?.click()}
                          data-testid={`credential-upload-${item.item_key}${item.state ? "-" + item.state : ""}`}>
                    {item.id ? 'Replace' : 'Upload'}
                  </Button>
                </>
              )}
              {item.kind === 'firearms' && item.status !== 'verified' && (
                <Button size="sm" variant="ghost" disabled={!!busy}
                        onClick={() => openQual(item)}
                        data-testid={`credential-enter-qual-${item.item_key}`}>
                  Enter qualification
                </Button>
              )}
            </div>
          </div>
        ))}
        {data.items.some((i) => i.status === 'rejected' && !i.returned_to_employee) && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="credential-return-pending">
            <p className="text-sm text-amber-900">
              {data.items.filter((i) => i.status === 'rejected' && !i.returned_to_employee).length} document(s) rejected and not yet returned.
              Send the officer a secure link to replace them.
            </p>
            <Button size="sm" disabled={notifying || !!busy} onClick={notifyReturn} data-testid="credential-return-notify-btn">
              {notifying ? 'Sending…' : 'Email return link to officer'}
            </Button>
          </div>
        )}
        {data.items.length > 0 && data.available && (
          <div className="pt-1">
            <Button size="sm" variant="outline" disabled={!!busy} onClick={issueReturnLink} data-testid="credential-return-link-btn">
              Copy employee upload link (return window)
            </Button>
          </div>
        )}

        {/* ── Verify confirmation dialog ──────────────────────────────────── */}
        <Dialog open={!!vTarget} onOpenChange={(o) => { if (!o) closeVerify(); }}>
          <DialogContent data-testid="credential-verify-dialog">
            <DialogHeader>
              <DialogTitle>Verify {vTarget?.item_label}</DialogTitle>
              <DialogDescription>
                Confirm the details below. The verified file will be added to the employee&apos;s record.
              </DialogDescription>
            </DialogHeader>

            {vCtx?.kind === 'license' && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label>License type</Label>
                  <Select value={vForm.license_type || ''} onValueChange={(v) => setVForm({ ...vForm, license_type: v })}>
                    <SelectTrigger data-testid="verify-license-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      {(vCtx.allowed_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>State</Label>
                    <Input value={vForm.state || ''} maxLength={2} placeholder="OK"
                           onChange={(e) => setVForm({ ...vForm, state: e.target.value.toUpperCase() })}
                           data-testid="verify-license-state" />
                  </div>
                  <div className="space-y-1">
                    <Label>License number</Label>
                    <Input value={vForm.license_number || ''}
                           onChange={(e) => setVForm({ ...vForm, license_number: e.target.value })}
                           data-testid="verify-license-number" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Expiration date</Label>
                  <Input type="date" value={vForm.expiration_date || ''}
                         onChange={(e) => setVForm({ ...vForm, expiration_date: e.target.value })}
                         data-testid="verify-license-exp" />
                  {licenseExpired && (
                    <p className="text-xs text-red-600" data-testid="verify-license-expired">
                      This credential is expired. Reject it with a reason instead.
                    </p>
                  )}
                </div>
                {vPreview?.summary && !licenseExpired && (
                  <p className="text-xs text-slate-600 bg-slate-50 rounded px-2 py-1" data-testid="verify-license-preview">
                    {vPreview.summary}
                  </p>
                )}
              </div>
            )}

            {vCtx?.kind === 'firearms' && (
              <div className="space-y-3">
                {vCtx.texas_note && (
                  <p className={`text-xs rounded px-2 py-1 ${vCtx.texas_prominent ? 'bg-amber-100 text-amber-900 font-medium border border-amber-300' : 'text-slate-600 bg-slate-50'}`}
                     data-testid="verify-texas-note">
                    {vCtx.texas_note}
                  </p>
                )}
                {vCtx.entered && Object.keys(vCtx.entered).length > 0 && (
                  <p className="text-xs text-slate-600 bg-slate-50 rounded px-2 py-1" data-testid="verify-qual-entered">
                    Officer entered{vCtx.entered_on_behalf && vCtx.entered_by_name ? ` (by ${vCtx.entered_by_name} on behalf of employee)` : ''}: review and correct below if needed.
                  </p>
                )}
                <div className="space-y-1">
                  <Label>Qualification date</Label>
                  <Input type="date" value={vForm.qualification_date || ''}
                         onChange={(e) => setVForm({ ...vForm, qualification_date: e.target.value })}
                         data-testid="verify-qual-date" />
                  {qualFuture && (
                    <p className="text-xs text-red-600" data-testid="verify-qual-future">
                      Qualification date cannot be in the future.
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label>Range or instructor</Label>
                  <Input value={vForm.range_or_instructor || ''}
                         onChange={(e) => setVForm({ ...vForm, range_or_instructor: e.target.value })}
                         placeholder="e.g. North Range / Officer J. Smith"
                         data-testid="verify-qual-range" />
                </div>
                <div className="space-y-1">
                  <Label>Result</Label>
                  <Select value={vForm.result || ''} onValueChange={(v) => setVForm({ ...vForm, result: v })}>
                    <SelectTrigger data-testid="verify-qual-result"><SelectValue placeholder="Pass or Fail" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pass">Pass</SelectItem>
                      <SelectItem value="Fail">Fail</SelectItem>
                    </SelectContent>
                  </Select>
                  {qualFailed && (
                    <p className="text-xs text-red-600" data-testid="verify-qual-failed">
                      A failed qualification cannot be verified. Reject it with a reason instead.
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label>Score (optional)</Label>
                  <Input value={vForm.score || ''} maxLength={20} placeholder="e.g. 248/250"
                         onChange={(e) => setVForm({ ...vForm, score: e.target.value })}
                         data-testid="verify-qual-score" />
                </div>
                {vCtx.has_card === false && (
                  <div className="space-y-2 rounded border border-slate-200 bg-slate-50 p-2" data-testid="verify-qual-roster">
                    <p className="text-xs text-slate-600">No card was uploaded. Verify against the range roster.</p>
                    <div className="space-y-1">
                      <Label>Roster date</Label>
                      <Input type="date" value={vForm.roster_date || ''}
                             onChange={(e) => setVForm({ ...vForm, roster_date: e.target.value })}
                             data-testid="verify-qual-roster-date" />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!vForm.verified_against_roster}
                             onChange={(e) => setVForm({ ...vForm, verified_against_roster: e.target.checked })}
                             data-testid="verify-qual-roster-confirm" />
                      I verified this qualification against the range roster.
                    </label>
                  </div>
                )}
              </div>
            )}

            {!vCtx && <p className="text-sm text-slate-500">Loading…</p>}

            <DialogFooter>
              <Button variant="outline" onClick={closeVerify} data-testid="verify-cancel">Cancel</Button>
              <Button disabled={!verifyValid() || vBusy} onClick={submitVerify} data-testid="verify-confirm">
                Verify &amp; add to record
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!qTarget} onOpenChange={(o) => { if (!o) setQTarget(null); }}>
          <DialogContent data-testid="credential-qual-dialog">
            <DialogHeader>
              <DialogTitle>Enter qualification — {qTarget?.item_label}</DialogTitle>
              <DialogDescription>
                Record the officer&apos;s last firearms qualification on their behalf (no card). You can verify it against the range roster afterward.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {qTarget?.texas_note && (
                <p className={`text-xs rounded px-2 py-1 ${qTarget.texas_prominent ? 'bg-amber-100 text-amber-900 font-medium border border-amber-300' : 'text-slate-600 bg-slate-50'}`}
                   data-testid="qual-texas-note">
                  {qTarget.texas_note}
                </p>
              )}
              <div className="space-y-1">
                <Label>Qualification date</Label>
                <Input type="date" value={qForm.qualification_date || ''} max={todayISO()}
                       onChange={(e) => setQForm({ ...qForm, qualification_date: e.target.value })}
                       data-testid="qual-date" />
              </div>
              <div className="space-y-1">
                <Label>Range or instructor</Label>
                <Input value={qForm.range_or_instructor || ''}
                       onChange={(e) => setQForm({ ...qForm, range_or_instructor: e.target.value })}
                       data-testid="qual-range" />
              </div>
              <div className="space-y-1">
                <Label>Result</Label>
                <Select value={qForm.result || ''} onValueChange={(v) => setQForm({ ...qForm, result: v })}>
                  <SelectTrigger data-testid="qual-result"><SelectValue placeholder="Pass or Fail" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pass">Pass</SelectItem>
                    <SelectItem value="Fail">Fail</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Score (optional)</Label>
                <Input value={qForm.score || ''} maxLength={20} placeholder="e.g. 248/250"
                       onChange={(e) => setQForm({ ...qForm, score: e.target.value })} data-testid="qual-score" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setQTarget(null)} data-testid="qual-cancel">Cancel</Button>
              <Button disabled={!qValid() || qBusy} onClick={submitQual} data-testid="qual-submit">Save qualification</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) setRejectTarget(null); }}>
          <DialogContent data-testid="credential-reject-dialog">
            <DialogHeader>
              <DialogTitle>Reject {rejectTarget?.item_label}</DialogTitle>
              <DialogDescription>
                The employee will be asked to re-upload and will see this reason on their upload link. Be specific and factual.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="credential-reject-reason">Reason for rejection</Label>
              <Textarea id="credential-reject-reason" rows={4} value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="e.g. The photo is too blurry to read the expiration date — please retake it in good light."
                        data-testid="credential-reject-reason" />
              <p className="text-xs text-slate-500" data-testid="credential-reject-hint">
                {rejectReason.trim().length < 10
                  ? `At least 10 characters required (${rejectReason.trim().length}/10).`
                  : 'Looks good.'}
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectTarget(null)} data-testid="credential-reject-cancel">Cancel</Button>
              <Button disabled={rejectReason.trim().length < 10 || !!busy} onClick={submitReject}
                      data-testid="credential-reject-submit">Reject document</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!returnResult} onOpenChange={(o) => { if (!o) setReturnResult(null); }}>
          <DialogContent data-testid="credential-return-no-email-dialog">
            <DialogHeader>
              <DialogTitle>No email on file</DialogTitle>
              <DialogDescription>
                This officer has no email on file, so the return link could not be sent.
                Give the officer the return link in person. It covers {returnResult?.items?.length || 0} item(s)
                and is valid until {returnResult?.expires_at ? new Date(returnResult.expires_at).toLocaleDateString() : ''}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label>Secure return link</Label>
              <div className="flex gap-2">
                <Input readOnly value={returnResult?.link || ''} data-testid="credential-return-link-value"
                       onFocus={(e) => e.target.select()} />
                <Button size="sm" data-testid="credential-return-link-copy"
                        onClick={() => { navigator.clipboard?.writeText(returnResult?.link || '').catch(() => {}); toast.success('Link copied'); }}>
                  Copy
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReturnResult(null)} data-testid="credential-return-no-email-close">Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default CredentialsPanel;
